import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  X,
  Users as UsersIcon,
  Mail,
  Phone,
  MapPin,
  Building2,
  RefreshCw,
  Inbox,
  FileText,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Send,
  Paperclip,
  Edit2,
  Trash2,
  Eye,
  Archive,
  AlertTriangle,
  Layers,
  Tag,
} from "lucide-react";
import { useViewMode } from "../hooks/useViewMode";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { Contact, Account } from "../types";
import { EmailThreadsModal } from "./EmailThreadsModal";
import { InvoiceListModal } from "./InvoiceListModal";
import { BillListModal } from "./BillListModal";
import { ContractorInviteModal } from "./ContractorInviteModal";
import { PeopleModal } from "./PeopleModal";
import { ContactViewModal } from "./ContactViewModal";
import { AttachmentsModal } from "./AttachmentsModal";
import {
  GoogleAddressAutocomplete,
  AddressDetails,
} from "./common/GoogleAddressAutocomplete";
import { NzbnAutocomplete } from "./common/NzbnAutocomplete";
import { offlineDataCache, CACHE_KEYS } from "../utils/offlineDataCache";
import { getContactTypeColor } from "../utils/statusColors";
import { RecordsPageHeader } from "./common/RecordsPageHeader";
import { RecordsFilters } from "./common/RecordsFilters";

interface ContactsProps {
  onClose?: () => void;
  mode?: "modal" | "page";
}

export const Contacts: React.FC<ContactsProps> = ({
  onClose,
  mode = "modal",
}) => {
  const { canAdd, canEdit, canDelete } = usePermissions();
  const { t } = useTranslation();
  const [view, setView] = useState<"list" | "add-edit">("list");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const { viewMode, setViewMode, isMobile } = useViewMode();
  const { user } = useAuth();
  const { navigate, currentPath } = useNavigation();
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [filterType, setFilterType] = useState<
    "all" | "client" | "supplier" | "both" | "neither"
  >("all");
  const [isClient, setIsClient] = useState(false);
  const [isSupplier, setIsSupplier] = useState(false);
  const [emailCounts, setEmailCounts] = useState<{ [key: string]: number }>({});
  const [attachmentCounts, setAttachmentCounts] = useState<{
    [key: string]: number;
  }>({});
  const [invoiceCounts, setInvoiceCounts] = useState<{ [key: string]: number }>(
    {},
  );
  const [billCounts, setBillCounts] = useState<{ [key: string]: number }>({});
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showEmailThreads, setShowEmailThreads] = useState(false);
  const [showInvoiceList, setShowInvoiceList] = useState(false);
  const [showBillList, setShowBillList] = useState(false);
  const [showContractorInvite, setShowContractorInvite] = useState(false);
  const [showPeople, setShowPeople] = useState(false);
  const [showContactView, setShowContactView] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [gmailConnected, setGmailConnected] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [syncingGmail, setSyncingGmail] = useState(false);
  const [syncingOutlook, setSyncingOutlook] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);

  // Ref for filters dropdown
  const filtersDropdownRef = useRef<HTMLDivElement>(null);

  // Bulk selection state
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(
    new Set(),
  );
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);

  // Group by state (for list view)
  const [groupBy, setGroupBy] = useState<
    "none" | "type" | "integration" | "account"
  >(() => {
    const saved = localStorage.getItem("contacts_groupBy");
    const validOptions = ["none", "type", "integration", "account"];
    return validOptions.includes(saved || "")
      ? (saved as "none" | "type" | "integration" | "account")
      : "none";
  });

  const pageSize = 20;

  const isSystemAdmin = user?.role === "System Administrator";

  // Persist group by selection
  useEffect(() => {
    localStorage.setItem("contacts_groupBy", groupBy);
  }, [groupBy]);

  // Filter helper functions
  const getActiveFilterCount = () => {
    let count = 0;
    if (filterType !== "all") count++;
    if (showInactive) count++;
    return count;
  };

  const clearAllFilters = () => {
    setFilterType("all");
    setShowInactive(false);
    setCurrentPage(1);
  };

  // Close filters dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filtersDropdownRef.current &&
        !filtersDropdownRef.current.contains(event.target as Node)
      ) {
        setShowFiltersDropdown(false);
      }
    };

    if (showFiltersDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showFiltersDropdown]);

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [addressFormatted, setAddressFormatted] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [nzbn, setNzbn] = useState("");
  const [isSubContractor, setIsSubContractor] = useState(false);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [newCapability, setNewCapability] = useState("");

  const handleAddressSelect = (details: AddressDetails) => {
    setStreet(details.street || "");
    setCity(details.city || "");
    setState(details.state || "");
    setPostalCode(details.postalCode || "");
    setCountry(details.country || "");
    setAddressFormatted(details.formatted);
  };

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Read URL query parameters and apply filters
  useEffect(() => {
    if (mode === "page" && currentPath.startsWith("/contacts")) {
      const urlParams = new URLSearchParams(window.location.search);

      console.log("[Contacts] Reading URL parameters:", {
        type: urlParams.get("type"),
        search: urlParams.get("search"),
      });

      // Handle type parameter (supplier, subcontractor, client)
      const typeParam = urlParams.get("type");
      if (typeParam === "supplier" || typeParam === "subcontractor") {
        // Subcontractors are suppliers, so filter as supplier
        console.log("[Contacts] Setting filterType to supplier");
        setFilterType("supplier");
      } else if (typeParam === "client") {
        console.log("[Contacts] Setting filterType to client");
        setFilterType("client");
      }

      // Handle search parameter
      const searchParam = urlParams.get("search");
      if (searchParam) {
        console.log("[Contacts] Setting searchQuery:", searchParam);
        setSearchInput(searchParam);
        setSearchQuery(searchParam);
      }
    }
  }, [currentPath, mode]);

  const fetchContacts = useCallback(async () => {
    setIsLoading(true);
    const isOnline = navigator.onLine;

    // Build cache key based on page, filter, and search
    const cacheKey = CACHE_KEYS.CONTACTS_PAGE(
      currentPage,
      filterType,
      searchQuery,
    );

    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any>(cacheKey);
    if (cached) {
      console.log(
        "[Contacts] Using cached contacts:",
        cached.contacts?.length || 0,
      );
      setContacts(cached.contacts || []);
      setTotalCount(cached.totalCount || 0);
      setTotalPages(cached.totalPages || 1);
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setMessage("Please sign in to view contacts.");
          if (!cached) {
            setContacts([]);
            setTotalCount(0);
            setTotalPages(1);
          }
          setIsLoading(false);
          return;
        }

        // Build query params
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: pageSize.toString(),
        });

        if (filterType !== "all") {
          params.append("type", filterType);
        }

        if (searchQuery.trim()) {
          params.append("search", searchQuery.trim());
        }

        if (showInactive) {
          params.append("showInactive", "true");
        }

        // Fetch contacts from unified endpoint with pagination and search
        const response = await fetch(`${apiUrl}/api/contacts?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();

        if (data.success) {
          const contactsList = data.data || [];
          const total = data.pagination?.total || contactsList.length;
          const pages = data.pagination?.totalPages || 1;

          // Cache the fresh data
          offlineDataCache.set(cacheKey, {
            contacts: contactsList,
            totalCount: total,
            totalPages: pages,
          });

          setContacts(contactsList);
          setTotalCount(total);
          setTotalPages(pages);

          // Reset bulk selection when fetching
          setSelectedContacts(new Set());
          setSelectAllActive(false);
          setShowBulkActions(false);
        } else {
          setMessage(
            data.message || "Failed to fetch contacts. Please try again.",
          );
          // If fetch fails and we don't have cached data, keep empty arrays
          if (!cached) {
            setContacts([]);
            setTotalCount(0);
            setTotalPages(1);
          }
        }
      } catch (error) {
        console.error("Failed to fetch contacts:", error);
        const errMsg = error instanceof Error ? error.message : String(error);
        const isNetworkError = errMsg === "Failed to fetch" || errMsg.includes("NetworkError");
        setMessage(
          isNetworkError
            ? "Failed to fetch contacts. Check that the backend is running and REACT_APP_API_URL is correct."
            : `Failed to fetch contacts. Please try again. (${errMsg})`
        );
        // If fetch fails and we don't have cached data, keep empty arrays
        if (!cached) {
          setContacts([]);
          setTotalCount(0);
          setTotalPages(1);
        }
      }
    } else {
      // Offline and no cache - show empty arrays
      if (!cached) {
        console.warn("[Contacts] Offline and no cached contacts available");
        setContacts([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    }

    setIsLoading(false);
  }, [currentPage, filterType, searchQuery, showInactive, apiUrl, pageSize]);

  const fetchEmailCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/api/email-threads/count-by-contact`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = await response.json();
      if (data.success) {
        const counts: { [key: string]: number } = {};
        Object.keys(data.data).forEach((contactId) => {
          counts[contactId] = data.data[contactId].count;
        });
        setEmailCounts(counts);
      }
    } catch (error) {
      console.error("Failed to fetch email counts:", error);
    }
  }, [apiUrl]);

  const fetchAttachmentCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const contactTypeParam =
        filterType === "all" ? "" : `&contactType=${filterType}`;
      const response = await fetch(
        `${apiUrl}/api/email-threads/attachments/count-by-contact?${contactTypeParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = await response.json();
      if (data.success) {
        setAttachmentCounts(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch attachment counts:", error);
    }
  }, [apiUrl, filterType]);

  const fetchInvoiceCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/invoices/count-by-client`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        const counts: { [key: string]: number } = {};
        Object.keys(data.data).forEach((clientId) => {
          counts[clientId] = data.data[clientId].count;
        });
        setInvoiceCounts(counts);
      }
    } catch (error) {
      console.error("Failed to fetch invoice counts:", error);
    }
  }, [apiUrl]);

  const fetchBillCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/bills/count-by-supplier`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        const counts: { [key: string]: number } = {};
        Object.keys(data.data).forEach((supplierId) => {
          counts[supplierId] = data.data[supplierId].count;
        });
        setBillCounts(counts);
      }
    } catch (error) {
      console.error("Failed to fetch bill counts:", error);
    }
  }, [apiUrl]);

  const fetchEmailIntegrationStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");

      const [gmailResponse, outlookResponse] = await Promise.all([
        fetch(`${apiUrl}/api/gmail/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${apiUrl}/api/outlook/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (gmailResponse.ok) {
        const gmailData = await gmailResponse.json();
        const isConnected = gmailData.data?.connected || false;
        setGmailConnected(isConnected);
        console.log("[Contacts] Gmail connected:", isConnected, gmailData);
      }

      if (outlookResponse.ok) {
        const outlookData = await outlookResponse.json();
        const isConnected = outlookData.data?.connected || false;
        setOutlookConnected(isConnected);
        console.log("[Contacts] Outlook connected:", isConnected, outlookData);
      }
    } catch (error) {
      console.error("Failed to fetch email integration status:", error);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (view === "list") {
      fetchContacts();
      fetchEmailCounts();
      fetchAttachmentCounts();
      fetchInvoiceCounts();
      fetchBillCounts();
      fetchEmailIntegrationStatus();
    }
  }, [
    view,
    currentPage,
    filterType,
    searchQuery,
    showInactive,
    fetchContacts,
    fetchEmailCounts,
    fetchAttachmentCounts,
    fetchInvoiceCounts,
    fetchBillCounts,
    fetchEmailIntegrationStatus,
  ]);

  // Auto-search: Debounce search input (search after 500ms of no typing)
  useEffect(() => {
    if (view !== "list") return;

    const timer = setTimeout(() => {
      if (searchInput.length >= 3) {
        setSearchQuery(searchInput);
        setCurrentPage(1);
      } else if (searchInput.length === 0) {
        // Clear search if input is empty
        setSearchQuery((prevQuery) => {
          if (prevQuery !== "") {
            return "";
          }
          return prevQuery;
        });
        // Reset to first page when clearing search
        setCurrentPage(1);
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchInput, view]);

  const handleSyncGmail = async (contact: Contact) => {
    if (syncingGmail) return;

    try {
      setSyncingGmail(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/gmail/sync-emails`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ contactId: contact._id }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage(`Gmail sync started for ${contact.name}`);
        setTimeout(() => {
          fetchEmailCounts();
          fetchContacts();
        }, 2000);
      } else {
        setMessage(data.message || "Failed to start Gmail sync");
      }
    } catch (error) {
      setMessage("Failed to start Gmail sync");
    } finally {
      setSyncingGmail(false);
    }
  };

  const handleSyncOutlook = async (contact: Contact) => {
    if (syncingOutlook) return;

    try {
      setSyncingOutlook(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/outlook/sync-emails`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ contactId: contact._id }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage(`Outlook sync started for ${contact.name}`);
        setTimeout(() => {
          fetchEmailCounts();
          fetchContacts();
        }, 2000);
      } else {
        setMessage(data.message || "Failed to start Outlook sync");
      }
    } catch (error) {
      setMessage("Failed to start Outlook sync");
    } finally {
      setSyncingOutlook(false);
    }
  };

  const handleViewEmails = (contact: Contact) => {
    setSelectedContact(contact);
    setShowEmailThreads(true);
  };

  const handleViewInvoices = (contact: Contact) => {
    setSelectedContact(contact);
    setShowInvoiceList(true);
  };

  const handleViewBills = (contact: Contact) => {
    setSelectedContact(contact);
    setShowBillList(true);
  };

  const handleViewPeople = (contact: Contact) => {
    setSelectedContact(contact);
    setShowPeople(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    setIsLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");

      const url = editingContact
        ? `${apiUrl}/api/contacts/${editingContact._id}`
        : `${apiUrl}/api/contacts`;

      const method = editingContact ? "PUT" : "POST";

      const payload: any = {
        name,
        isClient,
        isSupplier,
      };

      if (email) payload.email = email;
      if (phone) payload.phone = phone;
      if (taxNumber) payload.taxNumber = taxNumber;
      if (nzbn) payload.nzbn = nzbn;

      if (street || city || state || postalCode || country) {
        payload.address = {};
        if (street) payload.address.street = street;
        if (city) payload.address.city = city;
        if (state) payload.address.state = state;
        if (postalCode) payload.address.postalCode = postalCode;
        if (country) payload.address.country = country;
      }

      // Add supplier-specific fields if isSupplier is true
      if (isSupplier) {
        payload.isSubContractor = isSubContractor;
        payload.capabilities = capabilities;
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setMessage(
          editingContact ? t("contact.updateSuccess") : t("contact.addSuccess"),
        );
        resetForm();
        setTimeout(() => {
          setView("list");
          setMessage("");
        }, 1500);
      } else {
        setMessage(
          data.message ||
            (editingContact
              ? t("contact.updateFailed")
              : t("contact.addFailed")),
        );
      }
    } catch (error) {
      setMessage(
        editingContact
          ? t("contact.updateFailedRetry")
          : t("contact.addFailedRetry"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setStreet("");
    setAddressFormatted("");
    setCity("");
    setState("");
    setPostalCode("");
    setCountry("");
    setTaxNumber("");
    setNzbn("");
    setIsClient(false);
    setIsSupplier(false);
    setIsSubContractor(false);
    setCapabilities([]);
    setNewCapability("");
    setEditingContact(null);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setName(contact.name);
    setEmail(contact.email || "");
    setPhone(contact.phone || "");
    setStreet(contact.address?.street || "");
    setAddressFormatted(
      contact.address?.formatted || contact.address?.street || "",
    );
    setCity(contact.address?.city || "");
    setState(contact.address?.state || "");
    setPostalCode(contact.address?.postalCode || "");
    setCountry(contact.address?.country || "");
    setTaxNumber(contact.taxNumber || "");
    setNzbn((contact as any).nzbn || "");
    setIsClient(contact.isClient);
    setIsSupplier(contact.isSupplier);

    // Load supplier-specific fields if isSupplier
    if (contact.isSupplier) {
      setIsSubContractor(contact.isSubContractor || false);
      setCapabilities(contact.capabilities || []);
    }

    setView("add-edit");
  };

  const handleDelete = (contact: Contact) => {
    setContactToDelete(contact);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async (permanent: boolean) => {
    if (!contactToDelete) return;

    setIsLoading(true);
    setShowDeleteDialog(false);

    try {
      const token = localStorage.getItem("token");
      const endpoint = permanent
        ? `${apiUrl}/api/contacts/${contactToDelete._id}/permanent`
        : `${apiUrl}/api/contacts/${contactToDelete._id}`;

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        setMessage(
          permanent
            ? t("contact.permanentDeleteSuccess")
            : t("contact.deleteSuccess"),
        );
        fetchContacts();
        setTimeout(() => setMessage(""), 2000);
      } else {
        setMessage(data.message || t("contact.deleteFailed"));
      }
    } catch (error) {
      setMessage(t("contact.deleteFailedRetry"));
    } finally {
      setIsLoading(false);
      setContactToDelete(null);
    }
  };

  // Bulk selection handlers
  const handleToggleContactSelection = (contactId: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
    setSelectAllActive(false);
    setShowBulkActions(newSelected.size > 0 || selectAllActive);
  };

  const handleSelectAll = () => {
    const activeContacts = contacts.filter((c) => showInactive || c.isActive);
    if (selectAllActive || selectedContacts.size === activeContacts.length) {
      setSelectedContacts(new Set());
      setSelectAllActive(false);
      setShowBulkActions(false);
    } else {
      setSelectedContacts(new Set(activeContacts.map((c) => c._id)));
      setSelectAllActive(false);
      setShowBulkActions(true);
    }
  };

  const handleSelectAllAcrossPages = () => {
    setSelectAllActive(!selectAllActive);
    setSelectedContacts(new Set());
    setShowBulkActions(!selectAllActive);
  };

  const handleBulkDelete = async (permanent: boolean) => {
    setIsLoading(true);
    setShowBulkDeleteDialog(false);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const body: any = {
        permanent,
      };

      if (selectAllActive) {
        body.selectAll = true;
        body.showInactive = showInactive;
        body.type = filterType !== "all" ? filterType : undefined;
      } else {
        body.contactIds = Array.from(selectedContacts);
      }

      const response = await fetch(`${apiUrl}/api/contacts/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.success) {
        setMessage(
          data.message ||
            (permanent
              ? t("contact.bulkPermanentDeleteSuccess")
              : t("contact.bulkDeleteSuccess")),
        );
        setSelectedContacts(new Set());
        setSelectAllActive(false);
        setShowBulkActions(false);
        fetchContacts();
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(data.message || t("contact.bulkDeleteFailed"));
      }
    } catch (error) {
      setMessage(t("contact.bulkDeleteFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalContactCount = () => {
    return totalCount;
  };

  // Filtering is now done server-side via the API
  const filteredContacts = contacts;

  const getAccountName = (contact: Contact): string => {
    if (typeof contact.accountId === "object" && contact.accountId) {
      return (contact.accountId as Account).companyName;
    }
    return t("project.unknownAccount");
  };

  const getIntegrationName = (contact: Contact): string => {
    if (contact.syncedFromXero) return "Xero";
    if (contact.myobContactId) return "MYOB";
    return "";
  };

  // Group contacts based on groupBy setting
  const getGroupedContacts = (contactsList: Contact[]) => {
    if (groupBy === "none") {
      return {
        groups: [{ key: "all", label: "", contacts: contactsList }],
      };
    }

    if (groupBy === "type") {
      const groupsMap = new Map<string, Contact[]>();

      contactsList.forEach((contact) => {
        let typeKey = "";
        if (contact.isClient && contact.isSupplier) {
          typeKey = "both";
        } else if (contact.isClient) {
          typeKey = "client";
        } else if (contact.isSupplier) {
          typeKey = "supplier";
        } else {
          typeKey = "neither";
        }

        if (!groupsMap.has(typeKey)) {
          groupsMap.set(typeKey, []);
        }
        groupsMap.get(typeKey)!.push(contact);
      });

      const typeLabels: Record<string, string> = {
        both: t("contact.clientAndSupplier"),
        client: t("contact.clientOnly"),
        supplier: t("contact.supplierOnly"),
        neither: t("contact.neitherClientNorSupplier"),
      };

      const groups = Array.from(groupsMap.entries()).map(([key, contacts]) => ({
        key,
        label: typeLabels[key] || key,
        contacts,
      }));

      return { groups };
    }

    if (groupBy === "integration") {
      const groupsMap = new Map<string, Contact[]>();

      contactsList.forEach((contact) => {
        const integration = getIntegrationName(contact) || "none";
        if (!groupsMap.has(integration)) {
          groupsMap.set(integration, []);
        }
        groupsMap.get(integration)!.push(contact);
      });

      const integrationLabels: Record<string, string> = {
        Xero: "Xero",
        MYOB: "MYOB",
        none: t("contact.noIntegration"),
      };

      const groups = Array.from(groupsMap.entries()).map(([key, contacts]) => ({
        key,
        label: integrationLabels[key] || key,
        contacts,
      }));

      return { groups };
    }

    if (groupBy === "account" && isSystemAdmin) {
      const groupsMap = new Map<string, Contact[]>();

      contactsList.forEach((contact) => {
        const accountName = getAccountName(contact);
        if (!groupsMap.has(accountName)) {
          groupsMap.set(accountName, []);
        }
        groupsMap.get(accountName)!.push(contact);
      });

      const groups = Array.from(groupsMap.entries())
        .map(([key, contacts]) => ({
          key,
          label: key,
          contacts,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      return { groups };
    }

    return {
      groups: [{ key: "all", label: "", contacts: contactsList }],
    };
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t("contact.contactType")} *
        </label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isClient}
              onChange={(e) => setIsClient(e.target.checked)}
              className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t("contact.client")}
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isSupplier}
              onChange={(e) => setIsSupplier(e.target.checked)}
              className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t("contact.supplier")}
            </span>
          </label>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t("contact.typeHint")}
        </p>
      </div>

      <NzbnAutocomplete
        onEntitySelect={(entity) => {
          const entityName = entity.entityName || (entity as any).name || "";
          setName(entityName);
          setCountry("NZ");
          const nzbnVal = entity.nzbn || (entity as any).nzbn;
          if (nzbnVal) setNzbn(String(nzbnVal));
          if ((entity as any).taxNumber) setTaxNumber((entity as any).taxNumber);
          if ((entity as any).address) {
            const addr = (entity as any).address;
            setStreet(addr.street || addr.streetAddress || "");
            setCity(addr.city || addr.locality || "");
            setState(addr.state || addr.region || "");
            setPostalCode(addr.postalCode || addr.postCode || "");
            setAddressFormatted(addr.formatted || [addr.street, addr.city, addr.postalCode].filter(Boolean).join(", "));
          }
        }}
        placeholder="Search NZ business by name or NZBN..."
        country={country}
        showOnlyForNz={false}
      />
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {t("common.name")} *
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          placeholder={t("contact.enterName")}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {t("contact.email")}
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            placeholder={t("contact.emailPlaceholder")}
          />
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {t("contact.phone")}
          </label>
          <input
            type="tel"
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            placeholder={t("contact.phonePlaceholder")}
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="taxNumber"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {t("contact.taxNumber")}
        </label>
        <input
          type="text"
          id="taxNumber"
          value={taxNumber}
          onChange={(e) => setTaxNumber(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          placeholder={t("contact.taxNumberPlaceholder")}
        />
      </div>

      {isSupplier && (
        <>
          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="isSubContractor"
                checked={isSubContractor}
                onChange={(e) => setIsSubContractor(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label
                htmlFor="isSubContractor"
                className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("contact.markAsSubContractor")}
              </label>
            </div>

            {isSubContractor && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("contact.capabilities")}
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newCapability}
                    onChange={(e) => setNewCapability(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (
                          newCapability.trim() &&
                          !capabilities.includes(newCapability.trim())
                        ) {
                          setCapabilities([
                            ...capabilities,
                            newCapability.trim(),
                          ]);
                          setNewCapability("");
                        }
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-sm"
                    placeholder={t("contact.capabilitiesPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        newCapability.trim() &&
                        !capabilities.includes(newCapability.trim())
                      ) {
                        setCapabilities([
                          ...capabilities,
                          newCapability.trim(),
                        ]);
                        setNewCapability("");
                      }
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  >
                    {t("common.add")}
                  </button>
                </div>
                {capabilities.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {capabilities.map((capability, index) => (
                      <span
                        key={index}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${getContactTypeColor("contact")}`}
                      >
                        {capability}
                        <button
                          type="button"
                          onClick={() =>
                            setCapabilities(
                              capabilities.filter((_, i) => i !== index),
                            )
                          }
                          className="ml-2 text-primary-600 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t("contact.address")} ({t("common.optional")})
        </h4>

        <div className="space-y-3">
          <GoogleAddressAutocomplete
            value={addressFormatted || street}
            onChange={(value) => {
              setAddressFormatted(value);
              if (!value.includes(",")) {
                setStreet(value);
              }
            }}
            onPlaceSelect={handleAddressSelect}
            placeholder={t("contact.streetAddressPlaceholder")}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-sm"
          />

          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder={t("contact.cityPlaceholder")}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-sm"
            />
            <input
              type="text"
              placeholder={t("contact.statePlaceholder")}
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder={t("contact.postalCodePlaceholder")}
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-sm"
            />
            <input
              type="text"
              placeholder={t("contact.countryPlaceholder")}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100 text-sm"
            />
          </div>
        </div>
      </div>

      {message && (
        <div
          className={`p-3 rounded-md text-sm ${
            message.includes("success")
              ? "bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300"
              : "bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300"
          }`}
        >
          {message}
        </div>
      )}

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          <Building2 className="w-4 h-4" />
          <span>
            {isLoading
              ? t("common.saving")
              : editingContact
                ? t("contact.updateContact")
                : t("contact.addContact")}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            setView("list");
            setMessage("");
            resetForm();
          }}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );

  // If in page mode and showing add-edit, render as modal overlay
  if (mode === "page" && view === "add-edit") {
    return (
      <>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t("navigation.contacts")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t("contact.manageContacts")}
            </p>
          </div>
        </div>

        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingContact
                  ? t("contact.editContact")
                  : t("contact.addNewContact")}
              </h3>
              <button
                onClick={() => {
                  setView("list");
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {renderForm()}
          </div>
        </div>
      </>
    );
  }

  const containerClasses =
    mode === "page"
      ? "p-6 max-w-7xl mx-auto"
      : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50";

  const contentClasses =
    mode === "page"
      ? "w-full"
      : "bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto";

  return (
    <div className={containerClasses}>
      <div className={contentClasses}>
        {mode === "page" ? (
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t("navigation.contacts")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t("contact.manageContacts")}
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t("navigation.contacts")}
            </h3>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {view === "list" ? (
          <>
            <RecordsPageHeader
              recordCount={totalCount}
              recordLabel={t("contact.contact") || "contact"}
              recordLabelPlural={t("contact.contacts") || "contacts"}
              searchValue={searchInput}
              onSearchChange={setSearchInput}
              searchPlaceholder={
                t("contact.searchPlaceholder") || "Search contacts..."
              }
              viewMode={
                isMobile ? undefined : viewMode === "cards" ? "grid" : "list"
              }
              onViewModeChange={
                isMobile
                  ? undefined
                  : (mode) => setViewMode(mode === "grid" ? "cards" : "list")
              }
              showFilters={true}
              filtersOpen={showFiltersDropdown}
              onToggleFilters={() =>
                setShowFiltersDropdown(!showFiltersDropdown)
              }
              activeFilterCount={getActiveFilterCount()}
              activeFilters={[
                ...(filterType !== "all"
                  ? [
                      {
                        label:
                          filterType === "client"
                            ? t("contact.clients") || "Clients"
                            : filterType === "supplier"
                              ? t("contact.suppliers") || "Suppliers"
                              : filterType === "both"
                                ? t("contact.both") || "Both"
                                : t("contact.neither") || "Neither",
                        value: filterType,
                        onClear: () => {
                          setFilterType("all");
                          setCurrentPage(1);
                        },
                      },
                    ]
                  : []),
                ...(showInactive
                  ? [
                      {
                        label:
                          t("contact.showingInactive") || "Showing Inactive",
                        value: "inactive",
                        onClear: () => setShowInactive(false),
                      },
                    ]
                  : []),
              ]}
              filtersContent={
                <div className="relative" ref={filtersDropdownRef}>
                  <RecordsFilters
                    isOpen={showFiltersDropdown}
                    onClose={() => setShowFiltersDropdown(false)}
                    title={t("contact.filters") || "Filters"}
                    activeCount={getActiveFilterCount()}
                    onClearAll={clearAllFilters}
                    clearAllLabel={t("contact.clearAll") || "Clear all"}
                    fields={[
                      {
                        id: "type",
                        label: t("contact.contactType") || "Contact Type",
                        type: "select",
                        value: filterType,
                        onChange: (value) => {
                          setFilterType(value as typeof filterType);
                          setCurrentPage(1);
                        },
                        placeholder: t("contact.allTypes") || "All Types",
                        options: [
                          { value: "all", label: t("contact.all") || "All" },
                          {
                            value: "client",
                            label: t("contact.clients") || "Clients",
                          },
                          {
                            value: "supplier",
                            label: t("contact.suppliers") || "Suppliers",
                          },
                          { value: "both", label: t("contact.both") || "Both" },
                          {
                            value: "neither",
                            label: t("contact.neither") || "Neither",
                          },
                        ],
                      },
                      {
                        id: "status",
                        label: t("contact.status") || "Status",
                        type: "select",
                        value: showInactive ? "inactive" : "active",
                        onChange: (value) =>
                          setShowInactive(value === "inactive"),
                        options: [
                          {
                            value: "active",
                            label: t("contact.activeOnly") || "Active Only",
                          },
                          {
                            value: "inactive",
                            label:
                              t("contact.includeInactive") ||
                              "Include Inactive",
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              }
              primaryAction={
                canAdd("Contacts")
                  ? {
                      label: t("contact.addContact") || "Add Contact",
                      icon: Plus,
                      onClick: () => setView("add-edit"),
                    }
                  : undefined
              }
              secondaryActions={[
                {
                  label: t("contractorInvite.button") || "Invite Contractor",
                  icon: Send,
                  variant: "primary",
                  onClick: () => setShowContractorInvite(true),
                },
              ]}
            >
              {/* Inline Type Filter Tabs */}
              <div className="flex items-center space-x-2 flex-wrap">
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-1">
                  <button
                    onClick={() => {
                      setFilterType("all");
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      filterType === "all"
                        ? "bg-primary-600 text-white shadow-sm"
                        : "text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {t("contact.all") || "All"}
                  </button>
                  <button
                    onClick={() => {
                      setFilterType("client");
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      filterType === "client"
                        ? "bg-primary-600 text-white shadow-sm"
                        : "text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {t("contact.clients") || "Clients"}
                  </button>
                  <button
                    onClick={() => {
                      setFilterType("supplier");
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      filterType === "supplier"
                        ? "bg-primary-600 text-white shadow-sm"
                        : "text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {t("contact.suppliers") || "Suppliers"}
                  </button>
                  <button
                    onClick={() => {
                      setFilterType("both");
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      filterType === "both"
                        ? "bg-primary-600 text-white shadow-sm"
                        : "text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {t("contact.both") || "Both"}
                  </button>
                  <button
                    onClick={() => {
                      setFilterType("neither");
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      filterType === "neither"
                        ? "bg-primary-600 text-white shadow-sm"
                        : "text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {t("contact.neither") || "Neither"}
                  </button>
                </div>
              </div>
            </RecordsPageHeader>

            <div className="mt-6">
              {message && (
                <div
                  className={`p-3 rounded-md text-sm mb-4 ${
                    message.includes("success")
                      ? "bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300"
                      : "bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300"
                  }`}
                >
                  {message}
                </div>
              )}

              {isLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">
                    {t("contact.loading")}
                  </p>
                </div>
              ) : viewMode === "list" ? (
                <>
                  {/* Bulk Actions Bar */}
                  {showBulkActions && (
                    <div className="mb-4 p-3 bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50 border border-primary-200 dark:border-primary-800 rounded-lg flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {selectAllActive
                            ? t("contact.selectedAll", {
                                count: getTotalContactCount(),
                              })
                            : t("contact.selectedCount", {
                                count: selectedContacts.size,
                              })}
                        </span>
                        <button
                          onClick={() => setShowBulkDeleteDialog(true)}
                          className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
                        >
                          {t("contact.archiveSelected")}
                        </button>
                        <button
                          onClick={() => {
                            setBulkDeletePermanent(true);
                            setShowBulkDeleteDialog(true);
                          }}
                          className="px-3 py-1.5 text-sm bg-error-600 text-white rounded-md hover:bg-error-700"
                        >
                          {t("contact.permanentDeleteSelected")}
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedContacts(new Set());
                          setSelectAllActive(false);
                          setShowBulkActions(false);
                        }}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-600">
                          <th className="w-12 py-3 px-2">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={
                                  selectAllActive ||
                                  (selectedContacts.size > 0 &&
                                    selectedContacts.size === contacts.length)
                                }
                                onChange={
                                  selectAllActive
                                    ? handleSelectAllAcrossPages
                                    : handleSelectAll
                                }
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              {!selectAllActive && (
                                <button
                                  onClick={handleSelectAllAcrossPages}
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                >
                                  {t("contact.selectAll", {
                                    count: getTotalContactCount(),
                                  })}
                                </button>
                              )}
                            </div>
                          </th>
                          {isSystemAdmin && (
                            <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              <div className="flex items-center gap-1">
                                <span>{t("navigation.account")}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGroupBy(
                                      groupBy === "account"
                                        ? "none"
                                        : "account",
                                    );
                                  }}
                                  className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                                    groupBy === "account"
                                      ? "text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50"
                                      : "text-gray-600 dark:text-gray-400"
                                  }`}
                                  title={
                                    groupBy === "account"
                                      ? "Click to ungroup"
                                      : "Click to group by account"
                                  }
                                >
                                  <Layers className="w-3 h-3" />
                                </button>
                              </div>
                            </th>
                          )}
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("common.name")}
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            <div className="flex items-center gap-1">
                              <span>{t("contact.integration")}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGroupBy(
                                    groupBy === "integration"
                                      ? "none"
                                      : "integration",
                                  );
                                }}
                                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                                  groupBy === "integration"
                                    ? "text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50"
                                    : "text-gray-600 dark:text-gray-400"
                                }`}
                                title={
                                  groupBy === "integration"
                                    ? "Click to ungroup"
                                    : "Click to group by integration"
                                }
                              >
                                <Layers className="w-3 h-3" />
                              </button>
                            </div>
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            <div className="flex items-center gap-1">
                              <span>{t("contact.type")}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGroupBy(
                                    groupBy === "type" ? "none" : "type",
                                  );
                                }}
                                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                                  groupBy === "type"
                                    ? "text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50"
                                    : "text-gray-600 dark:text-gray-400"
                                }`}
                                title={
                                  groupBy === "type"
                                    ? "Click to ungroup"
                                    : "Click to group by type"
                                }
                              >
                                <Layers className="w-3 h-3" />
                              </button>
                            </div>
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("contact.email")}
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("contact.phone")}
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("contact.people")}
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("contact.threads")}
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("contact.attachments")}
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("contact.invoices")}
                          </th>
                          <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("contact.bills")}
                          </th>
                          {(gmailConnected || outlookConnected) && (
                            <th className="text-left py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                              {t("contact.emailSync")}
                            </th>
                          )}
                          <th className="text-right py-3 px-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("common.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getGroupedContacts(filteredContacts).groups.map(
                          (group) => {
                            const groupContactKeys = group.contacts.map(
                              (c) => c._id,
                            );
                            const allSelectedInGroup =
                              groupContactKeys.length > 0 &&
                              groupContactKeys.every((key) =>
                                selectedContacts.has(key),
                              );
                            const someSelectedInGroup =
                              groupContactKeys.some((key) =>
                                selectedContacts.has(key),
                              ) && !allSelectedInGroup;

                            const handleToggleGroup = () => {
                              const newSelected = new Set(selectedContacts);
                              groupContactKeys.forEach((key) => {
                                if (
                                  allSelectedInGroup &&
                                  newSelected.has(key)
                                ) {
                                  newSelected.delete(key);
                                } else if (
                                  !allSelectedInGroup &&
                                  !newSelected.has(key)
                                ) {
                                  newSelected.add(key);
                                }
                              });
                              setSelectedContacts(newSelected);
                            };

                            const groupHeader =
                              groupBy !== "none" ? (
                                <tr
                                  key={`group-${group.key}`}
                                  className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-600"
                                >
                                  <td className="px-2 py-3">
                                    <input
                                      type="checkbox"
                                      checked={allSelectedInGroup}
                                      ref={(input) => {
                                        if (input)
                                          input.indeterminate =
                                            someSelectedInGroup;
                                      }}
                                      onChange={handleToggleGroup}
                                      onClick={(e) => e.stopPropagation()}
                                      className="rounded border-gray-300 dark:border-gray-600 text-accent-600 focus:ring-blue-500"
                                      title={
                                        allSelectedInGroup
                                          ? "Deselect all in group"
                                          : "Select all in group"
                                      }
                                    />
                                  </td>
                                  <td
                                    colSpan={isSystemAdmin ? 13 : 12}
                                    className="px-4 py-3 font-semibold text-sm text-gray-900 dark:text-gray-100"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="flex items-center gap-2">
                                        {groupBy === "type" && (
                                          <Tag className="w-4 h-4" />
                                        )}
                                        {groupBy === "integration" && (
                                          <Building2 className="w-4 h-4" />
                                        )}
                                        {groupBy === "account" && (
                                          <Building2 className="w-4 h-4" />
                                        )}
                                        {group.label}
                                      </span>
                                      <span className="text-gray-600 dark:text-gray-400 font-normal">
                                        {group.contacts.length}{" "}
                                        {group.contacts.length === 1
                                          ? t("contact.contact")
                                          : t("contact.contacts")}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              ) : null;

                            return (
                              <React.Fragment key={group.key}>
                                {groupHeader}
                                {group.contacts.map((contact) => (
                                  <tr
                                    key={contact._id}
                                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <td className="py-3 px-2">
                                      <input
                                        type="checkbox"
                                        checked={
                                          selectAllActive ||
                                          selectedContacts.has(contact._id)
                                        }
                                        onChange={() =>
                                          handleToggleContactSelection(
                                            contact._id,
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                      />
                                    </td>
                                    {isSystemAdmin && (
                                      <td
                                        className="py-3 px-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const accountId =
                                            typeof contact.accountId ===
                                            "object"
                                              ? contact.accountId._id
                                              : contact.accountId;
                                          if (accountId) {
                                            navigate(`/accounts/${accountId}`);
                                          }
                                        }}
                                      >
                                        <span className="text-sm text-accent-600 dark:text-accent-400 hover:underline">
                                          {getAccountName(contact)}
                                        </span>
                                      </td>
                                    )}
                                    <td className="py-3 px-2">
                                      <div className="flex items-center space-x-2">
                                        <div
                                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${contact.syncedFromXero ? "bg-[#13B5EA]" : "bg-gray-100 dark:bg-gray-600"}`}
                                        >
                                          {contact.syncedFromXero ? (
                                            <span
                                              className="text-white font-bold text-sm"
                                              title="Xero"
                                            >
                                              X
                                            </span>
                                          ) : contact.myobContactId ? (
                                            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">
                                              MYOB
                                            </span>
                                          ) : (
                                            <Building2 className="w-4 h-4 text-primary-500 dark:text-primary-400" />
                                          )}
                                        </div>
                                        <div>
                                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {contact.name}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="py-3 px-2">
                                      <div className="flex justify-center">
                                        {getIntegrationName(contact) ===
                                          "Xero" && (
                                          <span
                                            className="inline-flex items-center justify-center w-fit min-w-0 px-2 py-1 rounded-full text-xs font-medium bg-[#cffafe] text-[#0e7490] dark:bg-[#164e63] dark:text-[#67e8f9]"
                                            title="Xero"
                                          >
                                            Xero
                                          </span>
                                        )}
                                        {getIntegrationName(contact) ===
                                          "MYOB" && (
                                          <span
                                            className="inline-flex items-center justify-center w-fit min-w-0 px-2 py-1 rounded-full text-xs font-medium bg-[#fee2e2] text-[#991b1b] dark:bg-[#7f1d1d] dark:text-[#fecaca]"
                                            title="MYOB"
                                          >
                                            MYOB
                                          </span>
                                        )}
                                        {!getIntegrationName(contact) && (
                                          <span className="text-gray-400">
                                            —
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-3 px-2">
                                      <div className="flex flex-col gap-1 items-start">
                                        {contact.isClient && (
                                          <span
                                            className={`inline-flex items-center justify-center w-fit px-2 py-1 rounded-full text-xs font-medium ${getContactTypeColor("client")}`}
                                          >
                                            {t("contact.client")}
                                          </span>
                                        )}
                                        {contact.isSupplier && (
                                          <span
                                            className={`inline-flex items-center justify-center w-fit px-2 py-1 rounded-full text-xs font-medium ${getContactTypeColor("supplier")}`}
                                          >
                                            {t("contact.supplier")}
                                          </span>
                                        )}
                                        {contact.isSupplier &&
                                          contact.isSubContractor && (
                                            <span
                                              className={`inline-flex items-center justify-center w-fit px-2 py-1 rounded-full text-xs font-medium ${getContactTypeColor("subContractor")}`}
                                            >
                                              {t("contact.subContractor")}
                                            </span>
                                          )}
                                        {!contact.isClient &&
                                          !contact.isSupplier && (
                                            <span
                                              className={`inline-flex items-center justify-center w-fit px-2 py-1 rounded-full text-xs font-medium ${getContactTypeColor("contact")}`}
                                            >
                                              {t("contact.contact")}
                                            </span>
                                          )}
                                      </div>
                                    </td>
                                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">
                                      {contact.email ? (
                                        <div className="flex items-center space-x-1">
                                          <Mail className="w-4 h-4" />
                                          <span className="truncate max-w-[200px]">
                                            {contact.email}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">—</span>
                                      )}
                                    </td>
                                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">
                                      {contact.phone ? (
                                        <div className="flex items-center space-x-1">
                                          <Phone className="w-4 h-4" />
                                          <span>{contact.phone}</span>
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">—</span>
                                      )}
                                    </td>
                                    <td className="py-3 px-2 text-sm text-gray-600 dark:text-gray-400">
                                      {contact.contactPersons &&
                                      contact.contactPersons.length > 0 ? (
                                        <button
                                          onClick={() =>
                                            handleViewPeople(contact)
                                          }
                                          className="flex items-center space-x-1 text-sm text-success-600 hover:text-success-700 dark:text-success-400 dark:hover:text-success-300"
                                        >
                                          <UsersIcon className="w-4 h-4" />
                                          <span className="font-medium">
                                            {contact.contactPersons.length}
                                          </span>
                                        </button>
                                      ) : (
                                        <div className="flex items-center space-x-1">
                                          <UsersIcon className="w-4 h-4" />
                                          <span className="text-gray-400">
                                            0
                                          </span>
                                        </div>
                                      )}
                                    </td>
                                    <td className="py-3 px-2">
                                      {emailCounts[contact._id] > 0 ? (
                                        <button
                                          onClick={() =>
                                            handleViewEmails(contact)
                                          }
                                          className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                        >
                                          <Inbox className="w-4 h-4" />
                                          <span className="font-medium">
                                            {emailCounts[contact._id]}
                                          </span>
                                        </button>
                                      ) : (
                                        <span className="text-gray-400 text-sm">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-3 px-2">
                                      {attachmentCounts[contact._id] > 0 ? (
                                        <button
                                          onClick={() => {
                                            setSelectedContact(contact);
                                            setShowAttachments(true);
                                          }}
                                          className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                                        >
                                          <Paperclip className="w-4 h-4" />
                                          <span className="font-medium">
                                            {attachmentCounts[contact._id]}
                                          </span>
                                        </button>
                                      ) : (
                                        <span className="text-gray-400 text-sm">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-3 px-2">
                                      {contact.isClient &&
                                      invoiceCounts[contact._id] > 0 ? (
                                        <button
                                          onClick={() =>
                                            handleViewInvoices(contact)
                                          }
                                          className="flex items-center space-x-1 text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
                                        >
                                          <FileText className="w-4 h-4" />
                                          <span className="font-medium">
                                            {invoiceCounts[contact._id]}
                                          </span>
                                        </button>
                                      ) : (
                                        <span className="text-gray-400 text-sm">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-3 px-2">
                                      {contact.isSupplier &&
                                      billCounts[contact._id] > 0 ? (
                                        <button
                                          onClick={() =>
                                            handleViewBills(contact)
                                          }
                                          className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                                        >
                                          <Receipt className="w-4 h-4" />
                                          <span className="font-medium">
                                            {billCounts[contact._id]}
                                          </span>
                                        </button>
                                      ) : (
                                        <span className="text-gray-400 text-sm">
                                          —
                                        </span>
                                      )}
                                    </td>
                                    {(gmailConnected || outlookConnected) && (
                                      <td className="py-3 px-2">
                                        <div className="flex items-center space-x-2">
                                          {gmailConnected && (
                                            <button
                                              onClick={() =>
                                                handleSyncGmail(contact)
                                              }
                                              disabled={syncingGmail}
                                              className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                                                syncingGmail
                                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                                                  : "bg-red-50 text-error-600 hover:bg-error-100 dark:bg-gray-700 dark:border dark:border-error-800/50 dark:text-error-400 dark:hover:bg-error-900/30"
                                              }`}
                                              title={t("contact.syncGmail")}
                                            >
                                              {syncingGmail ? (
                                                <>
                                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                                  <span>Gmail</span>
                                                </>
                                              ) : (
                                                <>
                                                  <Mail className="w-3 h-3" />
                                                  <span>Gmail</span>
                                                </>
                                              )}
                                            </button>
                                          )}
                                          {outlookConnected && (
                                            <button
                                              onClick={() =>
                                                handleSyncOutlook(contact)
                                              }
                                              disabled={syncingOutlook}
                                              className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                                                syncingOutlook
                                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                                                  : "bg-accent-50 text-accent-600 hover:bg-accent-100 dark:bg-gray-700 dark:border dark:border-accent-800/50 dark:text-accent-400 dark:hover:bg-accent-900/30"
                                              }`}
                                              title={t("contact.syncOutlook")}
                                            >
                                              {syncingOutlook ? (
                                                <>
                                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                                  <span>Outlook</span>
                                                </>
                                              ) : (
                                                <>
                                                  <Mail className="w-3 h-3" />
                                                  <span>Outlook</span>
                                                </>
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                    <td className="py-3 px-2 text-right">
                                      <div className="flex items-center justify-end space-x-2">
                                        <button
                                          onClick={() => {
                                            setSelectedContact(contact);
                                            setShowContactView(true);
                                          }}
                                          className="text-accent-600 dark:text-accent-400 hover:text-accent-900 dark:hover:text-accent-300"
                                          title={t("common.view")}
                                        >
                                          <Eye className="w-4 h-4" />
                                        </button>
                                        {canEdit("Contacts") && (
                                          <button
                                            onClick={() => handleEdit(contact)}
                                            className="text-accent-600 dark:text-accent-400 hover:text-accent-900 dark:hover:text-accent-300"
                                            title={t("common.edit")}
                                          >
                                            <Edit2 className="w-4 h-4" />
                                          </button>
                                        )}
                                        {canDelete("Contacts") && (
                                          <button
                                            onClick={() =>
                                              handleDelete(contact)
                                            }
                                            className="text-error-600 dark:text-error-400 hover:text-error-900 dark:hover:text-error-300 disabled:opacity-50"
                                            disabled={contact.syncedFromXero}
                                            title={
                                              contact.syncedFromXero
                                                ? t(
                                                    "contact.cannotDeleteXeroSynced",
                                                  )
                                                : t("common.delete")
                                            }
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          },
                        )}
                      </tbody>
                    </table>

                    {filteredContacts.length === 0 && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No contacts found
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Page {currentPage} of {totalPages} ({totalCount} total)
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1 || isLoading}
                          className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                        <button
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(totalPages, prev + 1),
                            )
                          }
                          disabled={currentPage === totalPages || isLoading}
                          className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredContacts.map((contact) => (
                      <div
                        key={contact._id}
                        className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col h-full"
                      >
                        {/* Header Section */}
                        <div className="flex items-start justify-between mb-3 flex-shrink-0">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div
                              className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${contact.syncedFromXero ? "bg-[#13B5EA]" : "bg-gray-100 dark:bg-gray-600"}`}
                            >
                              {contact.syncedFromXero ? (
                                <span
                                  className="text-white font-bold text-xl"
                                  title="Xero"
                                >
                                  X
                                </span>
                              ) : contact.myobContactId ? (
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                  MYOB
                                </span>
                              ) : (
                                <Building2 className="w-6 h-6 text-primary-500 dark:text-primary-400" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {contact.name}
                              </h3>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {contact.isClient && (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactTypeColor("client")}`}
                                  >
                                    {t("contact.client")}
                                  </span>
                                )}
                                {contact.isSupplier && (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactTypeColor("supplier")}`}
                                  >
                                    {t("contact.supplier")}
                                  </span>
                                )}
                                {contact.isSupplier &&
                                  contact.isSubContractor && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactTypeColor("subContractor")}`}
                                    >
                                      {t("contact.subContractor")}
                                    </span>
                                  )}
                                {!contact.isClient && !contact.isSupplier && (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getContactTypeColor("contact")}`}
                                  >
                                    {t("contact.contact")}
                                  </span>
                                )}
                                {getIntegrationName(contact) === "Xero" && (
                                  <span className="inline-flex items-center justify-center w-fit px-2 py-0.5 rounded-full text-xs font-medium bg-[#cffafe] text-[#0e7490] dark:bg-[#164e63] dark:text-[#67e8f9]">
                                    Xero
                                  </span>
                                )}
                                {getIntegrationName(contact) === "MYOB" && (
                                  <span className="inline-flex items-center justify-center w-fit px-2 py-0.5 rounded-full text-xs font-medium bg-[#fee2e2] text-[#991b1b] dark:bg-[#7f1d1d] dark:text-[#fecaca]">
                                    MYOB
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Contact Details Section */}
                        <div className="space-y-2 mb-4 flex-shrink-0 flex-1 min-h-0">
                          {/* Email and Phone - Full Width */}
                          {contact.email && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <Mail className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                              <span className="truncate">{contact.email}</span>
                            </div>
                          )}
                          {contact.phone && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <Phone className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                              <span className="truncate">{contact.phone}</span>
                            </div>
                          )}
                          
                          {/* Capabilities - Full Width */}
                          {contact.isSupplier &&
                            contact.isSubContractor &&
                            contact.capabilities &&
                            contact.capabilities.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Capabilities:
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {contact.capabilities.map(
                                    (capability, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-accent-50 text-accent-700 dark:bg-accent-900 dark:text-accent-300"
                                      >
                                        {capability}
                                      </span>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                          
                          {/* Invoice, Email Thread, Attachment, Person - 2 Column Grid */}
                          {(emailCounts[contact._id] > 0 ||
                            attachmentCounts[contact._id] > 0 ||
                            (contact.isClient && invoiceCounts[contact._id] > 0) ||
                            (contact.isSupplier && billCounts[contact._id] > 0) ||
                            (contact.contactPersons && contact.contactPersons.length > 0)) && (
                            <div className="grid grid-cols-2 gap-2">
                              {contact.isClient && invoiceCounts[contact._id] > 0 && (
                                <button
                                  onClick={() => handleViewInvoices(contact)}
                                  className="flex items-center space-x-1.5 text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300 transition-colors"
                                >
                                  <FileText className="w-4 h-4 flex-shrink-0" />
                                  <span className="font-medium truncate">
                                    {invoiceCounts[contact._id]}{" "}
                                    {invoiceCounts[contact._id] === 1
                                      ? t("contact.invoiceSingular")
                                      : t("contact.invoicePlural")}
                                  </span>
                                </button>
                              )}
                              {emailCounts[contact._id] > 0 && (
                                <button
                                  onClick={() => handleViewEmails(contact)}
                                  className="flex items-center space-x-1.5 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                                >
                                  <Inbox className="w-4 h-4 flex-shrink-0" />
                                  <span className="font-medium truncate">
                                    {emailCounts[contact._id]}{" "}
                                    {emailCounts[contact._id] === 1
                                      ? t("contact.emailThread")
                                      : t("contact.emailThreads")}
                                  </span>
                                </button>
                              )}
                              {attachmentCounts[contact._id] > 0 && (
                                <button
                                  onClick={() => {
                                    setSelectedContact(contact);
                                    setShowAttachments(true);
                                  }}
                                  className="flex items-center space-x-1.5 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                                >
                                  <Paperclip className="w-4 h-4 flex-shrink-0" />
                                  <span className="font-medium truncate">
                                    {attachmentCounts[contact._id]}{" "}
                                    {attachmentCounts[contact._id] === 1
                                      ? "attachment"
                                      : "attachments"}
                                  </span>
                                </button>
                              )}
                              {contact.contactPersons && contact.contactPersons.length > 0 && (
                                <button
                                  onClick={() => handleViewPeople(contact)}
                                  className="flex items-center space-x-1.5 text-sm text-success-600 hover:text-success-700 dark:text-success-400 dark:hover:text-success-300 transition-colors"
                                >
                                  <UsersIcon className="w-4 h-4 flex-shrink-0" />
                                  <span className="font-medium truncate">
                                    {contact.contactPersons.length}{" "}
                                    {contact.contactPersons.length === 1
                                      ? t("contact.person")
                                      : t("contact.people")}
                                  </span>
                                </button>
                              )}
                              {contact.isSupplier && billCounts[contact._id] > 0 && (
                                <button
                                  onClick={() => handleViewBills(contact)}
                                  className="flex items-center space-x-1.5 text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 transition-colors"
                                >
                                  <Receipt className="w-4 h-4 flex-shrink-0" />
                                  <span className="font-medium truncate">
                                    {billCounts[contact._id]}{" "}
                                    {billCounts[contact._id] === 1
                                      ? t("contact.billSingular")
                                      : t("contact.billPlural")}
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Address - Full Width */}
                          {contact.address?.city && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <MapPin className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                              <span className="truncate">
                                {contact.address.city}
                                {contact.address.state &&
                                  `, ${contact.address.state}`}
                              </span>
                            </div>
                          )}
                          
                          {/* No contact info */}
                          {!contact.email &&
                            !contact.phone &&
                            !contact.address?.city &&
                            emailCounts[contact._id] === 0 &&
                            attachmentCounts[contact._id] === 0 &&
                            invoiceCounts[contact._id] === 0 &&
                            billCounts[contact._id] === 0 &&
                            (!contact.contactPersons || contact.contactPersons.length === 0) && (
                              <div className="text-sm text-gray-400 dark:text-gray-500 italic">
                                No contact info
                              </div>
                            )}
                        </div>

                        {/* Action Buttons Section - Pushed to bottom */}
                        <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-600 flex items-center justify-end space-x-2 flex-shrink-0">
                          <button
                            onClick={() => {
                              setSelectedContact(contact);
                              setShowContactView(true);
                            }}
                            className="text-accent-600 dark:text-accent-400 hover:text-accent-900 dark:hover:text-accent-300 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            title={t("common.view")}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {canEdit("Contacts") && (
                            <button
                              onClick={() => handleEdit(contact)}
                              className="text-accent-600 dark:text-accent-400 hover:text-accent-900 dark:hover:text-accent-300 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                              title={t("common.edit")}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete("Contacts") && (
                            <button
                              onClick={() => handleDelete(contact)}
                              disabled={contact.syncedFromXero}
                              className="text-error-600 dark:text-error-400 hover:text-error-900 dark:hover:text-error-300 disabled:opacity-50 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                              title={
                                contact.syncedFromXero
                                  ? t("contact.cannotDeleteXeroSynced")
                                  : t("common.delete")
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    {filteredContacts.length === 0 && (
                      <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                        No contacts found
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Page {currentPage} of {totalPages} ({totalCount} total)
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={currentPage === 1 || isLoading}
                          className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                        <button
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(totalPages, prev + 1),
                            )
                          }
                          disabled={currentPage === totalPages || isLoading}
                          className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          renderForm()
        )}
      </div>

      {/* Email Threads Modal */}
      {showEmailThreads && selectedContact && (
        <EmailThreadsModal
          isOpen={showEmailThreads}
          onClose={() => {
            setShowEmailThreads(false);
            setSelectedContact(null);
          }}
          contactId={selectedContact._id}
          contactName={selectedContact.name}
          contactType={
            selectedContact.isClient
              ? "Client"
              : selectedContact.isSupplier
                ? "Supplier"
                : "Contact"
          }
        />
      )}

      {/* Invoice List Modal */}
      {showInvoiceList && selectedContact && selectedContact.isClient && (
        <InvoiceListModal
          isOpen={showInvoiceList}
          onClose={() => {
            setShowInvoiceList(false);
            setSelectedContact(null);
          }}
          clientId={selectedContact._id}
          clientName={selectedContact.name}
        />
      )}

      {/* Bill List Modal */}
      {showBillList && selectedContact && selectedContact.isSupplier && (
        <BillListModal
          isOpen={showBillList}
          onClose={() => {
            setShowBillList(false);
            setSelectedContact(null);
          }}
          supplierId={selectedContact._id}
          supplierName={selectedContact.name}
        />
      )}

      {/* Contractor Invite Modal */}
      {showContractorInvite && (
        <ContractorInviteModal
          onClose={() => setShowContractorInvite(false)}
          onSuccess={() => {
            setShowContractorInvite(false);
            fetchContacts(); // Refresh contacts to show updated contractor status
          }}
        />
      )}

      {/* People Modal */}
      {showPeople && selectedContact && (
        <PeopleModal
          isOpen={showPeople}
          onClose={() => {
            setShowPeople(false);
            setSelectedContact(null);
          }}
          contactName={selectedContact.name}
          contactPersons={selectedContact.contactPersons || []}
        />
      )}

      {/* Contact View Modal */}
      {showContactView && selectedContact && (
        <ContactViewModal
          isOpen={showContactView}
          onClose={() => {
            setShowContactView(false);
            setSelectedContact(null);
          }}
          contact={selectedContact}
          emailCounts={emailCounts}
          attachmentCounts={attachmentCounts}
          invoiceCounts={invoiceCounts}
          billCounts={billCounts}
          onViewEmails={() => {
            setShowContactView(false);
            setShowEmailThreads(true);
          }}
          onViewPeople={() => {
            setShowContactView(false);
            setShowPeople(true);
          }}
          onViewInvoices={() => {
            setShowContactView(false);
            setShowInvoiceList(true);
          }}
          onViewBills={() => {
            setShowContactView(false);
            setShowBillList(true);
          }}
        />
      )}

      {/* Attachments Modal */}
      {showAttachments && selectedContact && (
        <AttachmentsModal
          contact={selectedContact}
          onClose={() => {
            setShowAttachments(false);
            setSelectedContact(null);
          }}
        />
      )}

      {showDeleteDialog && contactToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            {/* Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-error-100 dark:bg-gray-700 dark:border dark:border-error-800/50 flex items-center justify-center mr-3">
                  <AlertTriangle className="w-6 h-6 text-error-600 dark:text-error-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t("contact.deleteContact")}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {contactToDelete.name}
                    {contactToDelete.email && ` (${contactToDelete.email})`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setContactToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {t("contact.confirmDeleteMessage")}
              </p>

              <div className="space-y-3">
                {/* Archive Option */}
                <button
                  onClick={() => handleConfirmDelete(false)}
                  disabled={
                    isLoading ||
                    !contactToDelete.isActive ||
                    contactToDelete.syncedFromXero
                  }
                  className={`w-full text-left p-4 border-2 rounded-lg transition-colors ${
                    !contactToDelete.isActive || contactToDelete.syncedFromXero
                      ? "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 cursor-not-allowed opacity-50"
                      : "border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                  } disabled:opacity-50`}
                  title={
                    contactToDelete.syncedFromXero
                      ? t("contact.cannotDeleteXeroSynced")
                      : !contactToDelete.isActive
                        ? t("contact.alreadyInactive")
                        : ""
                  }
                >
                  <div className="flex items-start">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        !contactToDelete.isActive ||
                        contactToDelete.syncedFromXero
                          ? "bg-gray-100 dark:bg-gray-700"
                          : "bg-primary-100 dark:bg-gray-700 dark:border dark:border-primary-800/50"
                      }`}
                    >
                      <Archive
                        className={`w-5 h-5 ${
                          !contactToDelete.isActive ||
                          contactToDelete.syncedFromXero
                            ? "text-gray-400 dark:text-gray-500"
                            : "text-primary-600 dark:text-primary-400"
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <h4
                        className={`font-semibold mb-1 ${
                          !contactToDelete.isActive ||
                          contactToDelete.syncedFromXero
                            ? "text-gray-400 dark:text-gray-500"
                            : "text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {t("contact.archiveContact")}
                        {!contactToDelete.isActive && (
                          <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">
                            ({t("contact.alreadyInactive")})
                          </span>
                        )}
                        {contactToDelete.syncedFromXero && (
                          <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">
                            ({t("contact.cannotDeleteXeroSynced")})
                          </span>
                        )}
                      </h4>
                      <p
                        className={`text-sm ${
                          !contactToDelete.isActive ||
                          contactToDelete.syncedFromXero
                            ? "text-gray-400 dark:text-gray-500"
                            : "text-gray-600 dark:text-gray-400"
                        }`}
                      >
                        {contactToDelete.syncedFromXero
                          ? t("contact.cannotDeleteXeroSyncedMessage")
                          : !contactToDelete.isActive
                            ? t("contact.alreadyInactiveMessage")
                            : t("contact.archiveContactMessage")}
                      </p>
                    </div>
                  </div>
                </button>

                {/* Permanent Delete Option */}
                <button
                  onClick={() => handleConfirmDelete(true)}
                  disabled={isLoading || contactToDelete.syncedFromXero}
                  className="w-full text-left p-4 border-2 border-red-200 dark:border-red-800 rounded-lg hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors disabled:opacity-50"
                  title={
                    contactToDelete.syncedFromXero
                      ? t("contact.cannotDeleteXeroSynced")
                      : ""
                  }
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-error-100 dark:bg-gray-700 dark:border dark:border-error-800/50 flex items-center justify-center mr-3">
                      <Trash2 className="w-5 h-5 text-error-600 dark:text-error-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {t("contact.permanentDeleteContact")}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {t("contact.permanentDeleteContactMessage")}
                      </p>
                      <div className="p-2 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 rounded text-xs text-error-700 dark:text-error-300">
                        ⚠️ {t("contact.permanentDeleteWarning")}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setContactToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Dialog */}
      {showBulkDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            {/* Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-error-100 dark:bg-gray-700 dark:border dark:border-error-800/50 flex items-center justify-center mr-3">
                  <AlertTriangle className="w-6 h-6 text-error-600 dark:text-error-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {bulkDeletePermanent
                      ? t("contact.permanentDeleteSelected")
                      : t("contact.archiveSelected")}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {selectAllActive
                      ? t("contact.selectedAll", { count: totalCount })
                      : t("contact.selectedCount", {
                          count: selectedContacts.size,
                        })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowBulkDeleteDialog(false);
                  setBulkDeletePermanent(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {selectAllActive
                  ? t("contact.confirmBulkDeleteAll", {
                      action: bulkDeletePermanent
                        ? t("contact.permanentlyDelete")
                        : t("contact.archive"),
                      count: totalCount,
                    })
                  : t("contact.confirmBulkDelete", {
                      action: bulkDeletePermanent
                        ? t("contact.permanentlyDelete")
                        : t("contact.archive"),
                      count: selectedContacts.size,
                    })}
              </p>
              {bulkDeletePermanent && (
                <p className="text-sm text-error-600 dark:text-error-400 mt-2">
                  {t("contact.permanentDeleteWarning")}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => {
                  setShowBulkDeleteDialog(false);
                  setBulkDeletePermanent(false);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={() => {
                  handleBulkDelete(bulkDeletePermanent);
                }}
                disabled={isLoading}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  bulkDeletePermanent
                    ? "bg-error-600 hover:bg-error-700"
                    : "bg-primary-600 hover:bg-primary-700"
                } disabled:opacity-50`}
              >
                {bulkDeletePermanent
                  ? t("contact.permanentDeleteSelected")
                  : t("contact.archiveSelected")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
